import { createTheme } from "@mui/material/styles";
import type {} from "@mui/x-date-pickers/themeAugmentation";

import { colors } from "./colors";

// Re-export für Abwärtskompatibilität
export { colors };

export const fonts = {
  primary: '"Inter", "Helvetica Neue", sans-serif',
  secondary: '"Roboto", "Helvetica", "Arial", sans-serif',
  monospace: '"JetBrains Mono", "Menlo", "Ubuntu Mono", monospace',
  brand: '"JetBrains Mono", monospace',
  weights: {
    light: 300,
    regular: 400,
    medium: 500,
    semiBold: 600,
    bold: 700,
  },
  sizes: {
    xs: "0.75rem",
    sm: "0.875rem",
    base: "1rem",
    lg: "1.125rem",
    xl: "1.25rem",
    "2xl": "1.5rem",
    "3xl": "1.875rem",
    "4xl": "2.25rem",
  },
} as const;

export const spacing = {
  xs: 0.5,
  sm: 1,
  md: 2,
  lg: 3,
  xl: 4,
  "2xl": 6,
  "3xl": 8,
} as const;

/**
 * Höhe der Zeilenbox in jedem Eingabefeld: MUI gibt dem Input-Slot `height: 1.4375em`
 * (bzw. `min-height` beim Select) und dem Label über `FormLabel` dieselbe `line-height`.
 * Beide Boxen sind damit gleich hoch – Grundlage dafür, dass derselbe Innenabstand Wert
 * UND Label mittig setzt.
 */
const FIELD_LINE_BOX = "1.4375em";

/**
 * Höhe JEDES einzeiligen Eingabefelds – TextField, Select, DatePicker, alles.
 * Der einzige Regler für die vertikale Feldgeometrie.
 */
const FIELD_HEIGHT = 44;

/**
 * Einzige Quelle für die Geometrie JEDES Outlined-Eingabefelds – TextField, Select,
 * DatePicker, alles. Die Theme-Overrides weiter unten (MuiOutlinedInput, MuiSelect,
 * MuiPickersOutlinedInput, MuiInputLabel) leiten sich alle hiervon ab, statt den Wert je
 * Feldtyp eigenständig zu wiederholen – ein abstraktes Formfeld, von dem jede konkrete
 * Feld-Komponente (`InputField`, `Dropdown`, `MultiSelectDropdown`, `Autocomplete`,
 * `InputFieldDate`, `InputFieldNumber`, ...) erbt, statt selbst Padding/Höhe zu erfinden.
 *
 * WICHTIG – keine feste `height` auf einem Input-Root setzen (auch nicht lokal per sx):
 * Die Feldhöhe ergibt sich ausschließlich aus `paddingY` + Zeilenbox. Der Wert eines
 * befüllten Feldes ist ein Flex-Kind des Input-Roots und bleibt bei jeder erzwungenen Höhe
 * optisch mittig, das Ruhe-Label (leeres, unfokussiertes Feld) hingegen ist absolut über
 * `translateY(paddingY)` positioniert und kennt die Feldhöhe nicht. Eine feste Höhe, die
 * nicht zu `paddingY` passt, lässt deshalb genau dieses Label aus der Mitte laufen – die
 * Ursache, aus der die Beschriftung leerer Felder früher zu weit unten saß.
 * Andere Feldhöhe gewünscht → `FIELD_HEIGHT` ändern, nicht lokal überschreiben.
 */
export const formField = {
  height: FIELD_HEIGHT,
  paddingX: 18,
  /**
   * Aus Höhe und Zeilenbox berechnet, statt als eigene Zahl geführt: so bleibt die
   * Zeilenbox – und damit sowohl der Wert als auch das Ruhe-Label – zwangsläufig mittig,
   * egal welche Höhe eingestellt ist.
   */
  paddingY: `calc((${FIELD_HEIGHT}px - ${FIELD_LINE_BOX}) / 2)`,
} as const;

/**
 * Hintergrund hinter dem hochgewanderten Label eines Outlined-Feldes (variant="floating").
 * MUI schneidet dafür eine Lücke ("Notch") in den Umrandungsstrich, deren Breite es aus der
 * gerenderten Label-Breite berechnet – bei unserer Schrift/unserem Innenabstand trifft das nicht
 * exakt, der Strich schimmert links/rechts neben dem Label noch durch. Einzige Stelle für diesen
 * Fix: jede floating-Feld-Komponente (`InputField`, `Dropdown`, `MultiSelectDropdown`,
 * `InputFieldNumber`, `InputFieldDate`, `Autocomplete`, ...) ruft das mit ihrer eigenen
 * Hintergrundfarbe auf, statt das Maskierungs-sx selbst zu wiederholen.
 */
export const floatingLabelMaskSx = (backgroundColor: string) => ({
  "& .MuiInputLabel-root": {
    backgroundColor,
    padding: "0 8px",
    "&.MuiInputLabel-shrink": {
      backgroundColor,
    },
  },
});

export const borderRadius = {
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  /**
   * Eckenrundung JEDES Eingabefelds (TextField, Select, Autocomplete, ToggleSwitch,
   * Editor-Feld, Dialog-Felder …) – und alles, was sich optisch auf Felder beziehen
   * muss, damit es dieselbe Formensprache hat: z. B. der Hintergrund einer selektierten
   * oder gehoverten Tabellenzeile, in der solche Felder liegen.
   *
   * Einziger Ort für diesen Wert. Niemals `25` (oder `"25px"`) direkt schreiben.
   */
  field: 25,
  full: 9999,
} as const;

export const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: colors.brand.primary,
      light: colors.brand.primaryLight,
      dark: colors.brand.primaryDark,
    },
    secondary: {
      main: colors.brand.secondary,
      light: colors.brand.secondaryLight,
      dark: colors.brand.secondary,
    },
    background: {
      default: colors.background.default,
      paper: colors.background.paper,
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
    },
    error: {
      main: colors.status.error.main,
      light: colors.status.error.light,
    },
    warning: {
      main: colors.status.warning.main,
      light: colors.status.warning.light,
    },
    success: {
      main: colors.status.success.main,
      light: colors.status.success.light,
    },
    info: {
      main: colors.status.info.main,
      light: colors.status.info.light,
    },
  },
  typography: {
    fontFamily: fonts.primary,
    h1: { fontFamily: fonts.primary, fontWeight: fonts.weights.bold },
    h2: { fontFamily: fonts.primary, fontWeight: fonts.weights.semiBold },
    h3: { fontFamily: fonts.primary, fontWeight: fonts.weights.semiBold },
    h4: { fontFamily: fonts.primary, fontWeight: fonts.weights.semiBold },
    h5: { fontFamily: fonts.primary, fontWeight: fonts.weights.medium },
    h6: { fontFamily: fonts.primary, fontWeight: fonts.weights.medium },
    body1: { fontFamily: fonts.primary, fontWeight: fonts.weights.regular },
    body2: { fontFamily: fonts.primary, fontWeight: fonts.weights.regular },
    button: {
      fontFamily: fonts.primary,
      fontWeight: fonts.weights.medium,
      textTransform: "none",
    },
  },
  shape: {
    borderRadius: borderRadius.md,
  },
  components: {
    // Einheitliche Farb-/Gewichtslogik für JEDES Feld-Label, an einer Stelle statt pro
    // Komponente neu definiert: ohne Wert gedimmt, mit Wert (MUI erkennt das selbst über
    // "filled") dunkel, im Fokus in der Akzentfarbe, bei Fehler rot, Pflichtfeld-Label fett.
    MuiFormControl: {
      styleOverrides: {
        root: {
          // Gegenstück zur Label-Farbe in MuiInputLabel: Ein schreibgeschütztes Feld bekommt
          // sein Label hellgrau zurück. MUI setzt `Mui-readOnly` am Input, nicht am Label –
          // erreichbar ist es deshalb nur über den gemeinsamen Vorfahren.
          "&:has(.Mui-readOnly) .MuiInputLabel-root:not(.Mui-focused)": {
            color: colors.text.disabled,
          },
        },
      },
    },
    MuiInputLabel: {
      // Jedes Feld in dieser App ist size="small" (siehe MuiTextField/MuiAutocomplete unten) –
      // TextField reicht das an sein Label durch, PickersTextField (DatePicker) tut das aber
      // nicht (rendert sein InputLabel ohne size-Prop, fällt auf MUIs "medium" zurück). Ohne
      // diesen Default bekommt nur das DatePicker-Label die MEDIUM-Ruheposition und sitzt
      // dadurch zu weit unten/außerhalb der kleineren Feldhöhe – zentral hier statt an jeder
      // DatePicker-Stelle einzeln `slotProps.inputLabel.size` zu setzen.
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          // Die Farbe sagt, ob das Feld bearbeitbar ist – nicht, ob etwas drinsteht. Ein
          // hellgraues Label liest sich als „hier ist nichts zu tun"; das gilt für ein
          // schreibgeschütztes Feld, nicht für ein leeres.
          //
          // Vorher hing die Farbe an MUIs Klasse `MuiFormLabel-filled`. Die setzt MUI aus
          // eigener Werterkennung, und die greift bei `InputFieldNumber` nie
          // (react-number-format verwaltet den Wert des <input> intern) – dort blieb das Label
          // deshalb auch mit Wert hellgrau, während es daneben dunkel war.
          color: colors.text.primary,
          fontWeight: fonts.weights.regular,
          "&.Mui-disabled": {
            color: colors.text.disabled,
          },
          "&.Mui-focused": {
            color: colors.border.focus,
          },
          "&.Mui-error": {
            color: colors.status.error.main,
          },
          "&.Mui-required": {
            fontWeight: fonts.weights.medium,
          },
          // MUI positioniert das noch nicht hochgewanderte Label (leeres, unfokussiertes Feld)
          // über eine feste, in `translate(14px, 9px)` einprogrammierte Ruheposition, passend zu
          // MUIs eigenem Default-Innenabstand (8.5px). Mit unserem größeren Innenabstand aus
          // `formField` (MuiOutlinedInput/MuiSelect/MuiPickersOutlinedInput) sitzt das Label
          // dadurch zu weit oben, da MUI diesen Wert nicht aus dem tatsächlichen Innenabstand
          // ableitet. Selektor mit gleicher Klassenzahl wie MUIs eigene Regel plus
          // `:not(shrink)`, damit nur die Ruheposition betroffen ist und das hochgewanderte
          // Label (fokussiert/befüllt) unangetastet bleibt.
          "&.MuiInputLabel-outlined.MuiInputLabel-sizeSmall:not(.MuiInputLabel-shrink)":
            {
              transform: `translate(${formField.paddingX}px, ${formField.paddingY}) scale(1)`,
            },
        },
      },
    },
    // Kein Scroll-Lock für Popups: `index.css` hält die Scrollbar am body dauerhaft sichtbar,
    // damit sich das Layout nie verschiebt. MUIs Lock würde sie beim Öffnen jedes Dialogs bzw.
    // Menüs ausblenden (`overflow: hidden`) und die Breite per gemessenem `padding-right`
    // ausgleichen – das trifft nicht alles (Header mit `100vw`), die Seite ruckelt dann beim
    // Öffnen und Schließen seitlich. Preis: Hinter einem offenen Dialog scrollt das Mausrad
    // die (abgedunkelte, nicht klickbare) Seite.
    MuiModal: { defaultProps: { disableScrollLock: true } },
    MuiDialog: { defaultProps: { disableScrollLock: true } },
    MuiPopover: { defaultProps: { disableScrollLock: true } },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: `0 2px 8px ${colors.shadow.card}`,
          transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
        },
      },
    },
    // DatePicker in x-date-pickers v9 nutzt PickersOutlinedInput statt OutlinedInput. Innenabstand
    // aus `formField` statt einer festen `height` – dieselbe (padding-getriebene) Größenlogik wie
    // MuiOutlinedInput unten, sonst weicht die Gesamthöhe des Datumsfelds von der der Text-/
    // Auswahlfelder daneben ab, obwohl beide optisch dieselbe Zeile bilden sollen.
    //
    // Anders als bei MuiOutlinedInput steckt der Innenabstand hier in zwei Slots: `root` trägt
    // das horizontale Padding, `sectionsContainer` (nicht `input` – das ist nur eine zusätzliche
    // Klasse ohne eigene styleOverrides-Wirkung) das vertikale, mit MUIs eigenem Default über
    // eine `inputSize`-Variante fest verdrahtet – ein normaler `input`-Override greift dort
    // deshalb ins Leere.
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.field,
          boxShadow: `0 2px 4px ${colors.shadow.light}`,
          transition: "box-shadow 0.2s ease-in-out",
          padding: `0 ${formField.paddingX}px`,
          "&:hover": {
            boxShadow: `0 4px 8px ${colors.shadow.medium}`,
          },
          "&:hover .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: colors.border.focus,
          },
          "&.Mui-focused .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: colors.border.focus,
            borderWidth: 2,
          },
        },
        sectionsContainer: {
          padding: `${formField.paddingY} 0`,
        },
        notchedOutline: {
          borderRadius: borderRadius.field,
        },
      },
    },
    // Pill-Radius + Schatten gilt für JEDES outlined Eingabefeld (TextField, Select, ...), nicht
    // nur für die eigens gestylten common/inputs-Wrapper. Die Wrapper setzen denselben Look
    // bisher zusätzlich lokal per sx (das bleibt harmlos redundant) – hier greift er aber auch,
    // wenn ein TextField/Select mal ohne Wrapper verwendet wird, statt in MUI-Standardoptik zu
    // fallen und neben den Wrapper-Feldern aufzufallen.
    //
    // Innenabstand ebenfalls hier zentral (aus `formField`): MUIs Standard für size="small"
    // (8.5px 14px, keine feste Höhe) wirkt bei der Pill-Form zu eng – Text sitzt zu nah an Rand
    // und Rundung (dortiges lokales `& .MuiSelect-select`-sx der Select-Variante von Dropdown
    // überschreibt diesen Wert weiterhin gezielt, falls doch mal ein anderer Wert gebraucht
    // wird).
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.field,
          boxShadow: `0 2px 4px ${colors.shadow.light}`,
          transition: "box-shadow 0.2s ease-in-out",
          "&:hover": {
            boxShadow: `0 4px 8px ${colors.shadow.medium}`,
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderRadius: borderRadius.field,
          },
          "& .MuiOutlinedInput-input": {
            padding: `${formField.paddingY} ${formField.paddingX}px`,
          },
        },
      },
    },
    // Select rendert clientseitig als <div>, nicht <input> – MUI legt dafür einen eigenen
    // Default-Innenabstand auf denselben `.MuiOutlinedInput-input`-Slot, der bei gleicher CSS-
    // Spezifität durch spätere Einfügereihenfolge den Override oben schlägt. Ohne diesen
    // eigenen MuiSelect-Override bleibt der Text vertikal oben hängen statt zentriert zu sein –
    // betrifft jedes Select-basierte Feld (Dropdown mit variant="floating", MultiSelectDropdown,
    // jedes rohe MUI Select), nicht nur ein einzelnes Formular.
    MuiSelect: {
      styleOverrides: {
        select: {
          paddingTop: formField.paddingY,
          paddingBottom: formField.paddingY,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: "small",
      },
    },
    // Autocomplete verteilt den Innenabstand anders als jedes andere Feld: er sitzt am
    // Input-Root (dort brauchen ausgewählte Chips Platz innerhalb der Umrandung), das
    // eigentliche <input> bekommt nur einen kleinen Restwert – und diese Regel ist
    // spezifischer als der MuiOutlinedInput-Override oben, gewinnt also gegen ihn. Ohne
    // Umlegen auf `formField` wäre das Feld niedriger als Text-/Auswahlfelder daneben und das
    // Ruhe-Label – das weiterhin aus `formField` positioniert wird – säße daneben.
    // Rechts kein eigener Wert: dort hält MUI selbst Platz für Aufklapp-/Löschen-Icon frei.
    MuiAutocomplete: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        // Die Größen-Klasse muss mit im Selektor stehen: MUIs eigene Regel führt sie
        // ebenfalls und wäre sonst spezifischer als dieser Override.
        root: {
          "& .MuiOutlinedInput-root, & .MuiOutlinedInput-root.MuiInputBase-sizeSmall":
            {
              paddingTop: formField.paddingY,
              paddingBottom: formField.paddingY,
              paddingLeft: formField.paddingX,
              "& .MuiAutocomplete-input": {
                padding: 0,
              },
            },
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        size: "small",
      },
    },
    MuiRadio: {
      defaultProps: {
        size: "small",
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: borderRadius.md,
          fontWeight: fonts.weights.medium,
          // Dezentere outlined Buttons (v9: über CSS-Klassen-Selektor)
          "&.MuiButton-outlinedPrimary": {
            borderColor: colors.brand.teal300,
            color: colors.brand.teal600,
            "&:hover": {
              borderColor: colors.brand.primary,
              backgroundColor: colors.brand.teal50,
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: colors.background.tableHeader,
          fontWeight: fonts.weights.semiBold,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        hover: {
          "&:hover": {
            backgroundColor: colors.state.hoverPrimary,
          },
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        slotProps: {
          transition: { timeout: { enter: 150, exit: 100 } },
        },
      },
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.tooltip.background,
          border: `1px solid ${colors.tooltip.border}`,
          borderRadius: borderRadius.md,
          color: colors.tooltip.text,
          fontSize: fonts.sizes.sm,
          fontWeight: fonts.weights.regular,
          padding: "12px 16px",
          boxShadow: `0 12px 28px ${colors.shadow.medium}, 0 2px 6px ${colors.shadow.light}`,
        },
        arrow: {
          color: colors.tooltip.background,
          filter: `drop-shadow(0 1px 1px ${colors.shadow.light})`,
        },
      },
    },
  },
});

export const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case "abgeschlossen":
    case "success":
      return colors.status.success;
    case "laufend":
    case "info":
      return colors.status.info;
    case "entwurf":
    case "warning":
      return colors.status.warning;
    case "error":
      return colors.status.error;
    default:
      return colors.status.info;
  }
};

export const getFontWeight = (weight: keyof typeof fonts.weights) => {
  return fonts.weights[weight];
};

export const getFontSize = (size: keyof typeof fonts.sizes) => {
  return fonts.sizes[size];
};

export const setCSSVariables = () => {
  if (typeof document !== "undefined") {
    const root = document.documentElement;
    Object.entries(cssVariables).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });
  }
};

export const cssVariables = {
  "--color-brand-primary": colors.brand.primary,
  "--color-brand-primary-light": colors.brand.primaryLight,
  "--color-brand-primary-dark": colors.brand.primaryDark,
  "--color-brand-secondary": colors.brand.secondary,

  "--color-background-default": colors.background.default,
  "--color-background-paper": colors.background.paper,
  "--color-background-tableHeader": colors.background.tableHeader,

  "--color-text-primary": colors.text.primary,
  "--color-text-secondary": colors.text.secondary,
  "--color-text-disabled": colors.text.disabled,
  "--color-text-label": colors.text.label,

  "--color-status-success": colors.status.success.main,
  "--color-status-warning": colors.status.warning.main,
  "--color-status-error": colors.status.error.main,
  "--color-status-info": colors.status.info.main,

  "--color-border-light": colors.border.light,
  "--color-border-medium": colors.border.medium,
  "--color-border-divider": colors.border.divider,

  "--color-utility-loading": colors.utility.loading,
  "--color-utility-lightGray": colors.utility.lightGray,
  "--color-utility-mediumGray": colors.utility.mediumGray,
  "--color-utility-darkGray": colors.utility.darkGray,
  "--color-utility-darkerGray": colors.utility.darkerGray,

  "--font-primary": fonts.primary,
  "--font-secondary": fonts.secondary,
  "--font-monospace": fonts.monospace,

  "--font-weight-light": fonts.weights.light.toString(),
  "--font-weight-regular": fonts.weights.regular.toString(),
  "--font-weight-medium": fonts.weights.medium.toString(),
  "--font-weight-semiBold": fonts.weights.semiBold.toString(),
  "--font-weight-bold": fonts.weights.bold.toString(),
} as const;
